import { Flex, Center, Spinner } from "@chakra-ui/react";
import { sendWarningReport } from "../../../../utils/reporting";
import { getDelayFromHours } from "../../../utils/delayFromHours";
import { getFlightIdentifiers } from "../../../utils/flightIdentifier";
import { updateClaimDetails, usePreEligibilityDataSnapshot } from "../state/preEligibilityState";
import { useCreateClaimEnquiry } from "../../../modules/hooks/useCreateClaimEnquiry";
import { FunnelStepContainer } from "../../../components/funnel/ui-elements/FunnelStepContainer";
import { StepHeader } from "v2/components/funnel/intake/flight-summary/StepHeader";
import { DisruptedFlightSummary } from "v2/components/funnel/intake/flight-summary/DisruptedFlightSummary";
import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import { FunnelStepBackControl } from "v2/components/funnel/ui-elements/FunnelStepControls";
import { Currency } from "app-settings/localesAndCurrencies";
import { appSettingsSnapshot } from "v2/settings";
import { useFetchBarcodeLeadsFlight } from "v2/modules/hooks/useFetchBarcodeLeadsFlight";
import { useState } from "react";
import { useFormHandlers } from "v2/modules/form/useFormHandlers";
import { z } from "zod";
import { ErrorNotification } from "v2/components/funnel/ui-elements/ErrorNotification";
import dataLayer from "tracking/tools/dataLayer";
import { simplifiedFLowEvents } from "tracking/events/simplifiedFlow";
import { useFunnel } from "@airhelp/funnel";
import { setShouldCutInformedBeforehandStep } from "../state/appState";
import { isRtl } from "utils/getLocaleDirection";
import { DisruptedCTA } from "./disrupted-flight-detected/DisruptedCTA";

export function DisruptedFlightDetectedStep() {
  const funnels = useFunnel();
  const { t } = useTranslation();
  const { itineraryFlights, claimDetails } = usePreEligibilityDataSnapshot();
  const { createClaimEnquiry } = useCreateClaimEnquiry();
  const { currency: userCurrency, locale } = appSettingsSnapshot();
  const flightsIdentifiers = itineraryFlights?.map((itineraryFlight) =>
    getFlightIdentifiers({
      flightDate: itineraryFlight.flightDate,
      flightNumber: itineraryFlight.flightNumber,
      airlineCode: itineraryFlight.airline?.code,
      arrivalAirportIata: itineraryFlight.arrivalAirport?.iata,
      departureAirportIata: itineraryFlight.departureAirport?.iata,
    }),
  );
  const { barcodeLeadsData, isFetched } = useFetchBarcodeLeadsFlight(flightsIdentifiers);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const claimType = claimDetails.potentialClaimType === "cancelled" ? "cancelled" : "delayed";
  const isCancelled = claimType === "cancelled";
  const potentialRegulation = barcodeLeadsData?.potentialRegulation || "ec261";
  const hideCompensation =
    barcodeLeadsData?.eligibilityCheckResult?.finalEligibility === "ineligible" ||
    potentialRegulation === "mcxx" ||
    potentialRegulation === "ca_goodwill" ||
    potentialRegulation === "us_goodwill" ||
    potentialRegulation === "saudi_arabia";

  const currency: Currency = (() => {
    switch (barcodeLeadsData?.potentialRegulation) {
      case "ec261":
        return "EUR";
      case "brazil":
        return "BRL";
      case "uk261":
        return "GBP";
      default:
        return userCurrency !== "CAD" ? userCurrency : "EUR";
    }
  })();

  const disruptedFlight = barcodeLeadsData?.flights.find((flight) => flight.disrupted);

  const informedUpTo6Days =
    disruptedFlight?.informed !== undefined &&
    disruptedFlight?.informed !== null &&
    disruptedFlight?.informed <= 6;

  const cutInformedBeforehandStep =
    informedUpTo6Days &&
    barcodeLeadsData?.eligibilityCheckResult?.finalEligibility === "eligible" &&
    barcodeLeadsData?.potentialRegulation !== "brazil";

  const { onSubmit, showError } = useFormHandlers({
    schema: z.never(),
    async onSubmit() {
      setIsSubmitting(true);
      dataLayer.push(
        isCancelled
          ? simplifiedFLowEvents.claim_for_cancellation_clicked_cx
          : simplifiedFLowEvents.claim_for_delay_clicked_dy,
      );
      if (
        cutInformedBeforehandStep ||
        barcodeLeadsData?.eligibilityCheckResult?.finalEligibility === "ineligible" ||
        (!isCancelled && barcodeLeadsData?.potentialRegulation !== "brazil")
      ) {
        try {
          const updatedClaimDetails = {
            ...claimDetails,
            claimValid: barcodeLeadsData?.eligibilityCheckResult?.finalEligibility === "eligible",
            potentialRegulation:
              barcodeLeadsData?.potentialRegulation ?? claimDetails.potentialRegulation ?? "ec261",
            claimType: isCancelled ? ("cancelled" as const) : ("delayed" as const),
            totalDelay: disruptedFlight?.delayMins
              ? getDelayFromHours(disruptedFlight?.delayMins / 60) || "never"
              : "never",
            informedBeforehand: cutInformedBeforehandStep ? ("lt6" as const) : null,
          };

          setShouldCutInformedBeforehandStep();

          await createClaimEnquiry({
            itineraryFlights,
            claimDetails: updatedClaimDetails,
          });
        } catch (error) {
          sendWarningReport("Error submitting claim on DisruptedFlightDetectedStep", error);
          setIsSubmitting(false);
        }
      } else {
        updateClaimDetails({
          potentialRegulation: barcodeLeadsData?.potentialRegulation || "ec261",
          claimType: claimType,
          totalDelay: disruptedFlight?.delayMins
            ? getDelayFromHours(disruptedFlight?.delayMins / 60) || "never"
            : "never",
          prefilled: true,
        });
        funnels.stepForward();
        setIsSubmitting(false);
      }
    },
  });

  if ((!barcodeLeadsData || !barcodeLeadsData?.eligibilityCheckResult) && isFetched) {
    sendWarningReport("Missing data on DisruptedFlightDetectedStep");
    funnels.stepForward();
    return;
  }

  const onAnotherClaimReasonClick = () => {
    updateClaimDetails({
      claimType: undefined,
      totalDelay: undefined,
      prefilled: undefined,
    });
    dataLayer.push(
      isCancelled
        ? simplifiedFLowEvents.claim_for_other_reason_clicked_cx
        : simplifiedFLowEvents.claim_for_other_reason_clicked_dy,
    );
    funnels.stepForward();
  };

  if (!barcodeLeadsData) {
    return (
      <Center>
        <Spinner variant="contentMedium" />
      </Center>
    );
  }

  function onBackClick() {
    updateClaimDetails({
      claimType: undefined,
      totalDelay: undefined,
      prefilled: undefined,
    });
  }

  return (
    <FunnelStepContainer>
      <StepHeader disruptionReason={isCancelled ? "cancelled" : "delayed"} />
      <DisruptedFlightSummary
        currency={currency}
        airlineName={disruptedFlight?.airline?.name || ""}
        airlineCode={disruptedFlight?.airline?.iata || ""}
        flightNumber={disruptedFlight?.flightNumber}
        arrivalTime={disruptedFlight?.arrivalTime}
        actualArrivalTime={disruptedFlight?.actualArrivalTime}
        arrivalAirportIata={disruptedFlight?.arrivalAirport.iata || ""}
        arrivalAirportCity={disruptedFlight?.arrivalAirport.city || ""}
        departureAirportIata={disruptedFlight?.departureAirport.iata || ""}
        departureAirportCity={disruptedFlight?.departureAirport.city || ""}
        delayMins={disruptedFlight?.delayMins}
        distanceKm={disruptedFlight?.distanceKm}
        disruptionReason={isCancelled ? "cancelled" : "delayed"}
        potentialCompensation={
          barcodeLeadsData?.eligibilityCheckResult?.compensationInCurrencies?.[currency]
        }
        hideCompensation={hideCompensation}
        isRtl={isRtl(locale)}
      />

      {showError && <ErrorNotification message={t("components.funnel_step.try_again")} />}

      <Flex justify="space-between" direction={{ base: "column", md: "row" }}>
        {!isMobile && <FunnelStepBackControl customOnClick={onBackClick} />}
        <DisruptedCTA
          t={t}
          onAnotherClaimReasonClick={onAnotherClaimReasonClick}
          onSubmit={onSubmit}
          isSubmitting={isSubmitting}
          isCancelled={isCancelled}
          disruptionReason={claimType}
        />
      </Flex>
    </FunnelStepContainer>
  );
}
